/**
 * Review moderation — `/v1/admin/reviews*`, `/v1/admin/review-responses*`,
 * `/v1/admin/rating-dimensions*`.
 *
 * **Publishing is its own mutation, not a status write.** It moves the pandit's public rating
 * and may raise a quality flag, so it is a different act from rejecting — and a single
 * `setStatus(id, status)` would let a screen do all of that by sending a string.
 *
 * **Redaction is the moderator's text, sent whole.** There is no client-side redaction helper
 * here on purpose: a regex that strips the phrases it recognises will miss the ones it does not,
 * and the moderator would be publishing something they believe was cleaned. What they typed is
 * what gets stored, and the server lints it again before it goes public.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { authedClient } from '../../auth/session';
import { keys } from '../queryClient';
import type { components } from '../../../api/generated/schema';

export type AdminReviewRow = components['schemas']['AdminReviewRow'];
export type DimensionRow = components['schemas']['DimensionRow'];
export type QualityFlagGroup = components['schemas']['QualityFlagGroupOut'];
type QualityFlagQueue = components['schemas']['QualityFlagQueueOut'];
export type DivergenceReport = components['schemas']['DivergenceReportOut'];
type ReviewList = components['schemas']['AdminReviewListResponse'];
type DimensionList = components['schemas']['DimensionListResponse'];
export type ReviewSummary = components['schemas']['AdminReviewSummary'];

export interface ReviewQuery extends Record<string, unknown> {
  review_status: string;
  page: number;
  page_size: number;
}

/** Exported for its own test — the queue narrows on the server, never in the browser. */
export function reviewSearch(params: ReviewQuery): string {
  return new URLSearchParams({
    review_status: params.review_status,
    page: String(params.page),
    page_size: String(params.page_size),
  }).toString();
}

export function useReviewQueue(params: ReviewQuery) {
  return useQuery({
    queryKey: keys.reviews.queue(params),
    queryFn: () => authedClient.request<ReviewList>(`/v1/admin/reviews?${reviewSearch(params)}`),
  });
}

export function useRatingDimensions() {
  return useQuery({
    queryKey: keys.reviews.dimensions(),
    queryFn: () => authedClient.request<DimensionList>('/v1/admin/rating-dimensions'),
  });
}

/** Open quality flags by pandit and type — the hand-off from support to supply. */
/** The design's KPI row: counted on the server over the tables, not over the page held here. */
export function useReviewSummary() {
  return useQuery({
    queryKey: keys.reviews.summary(),
    queryFn: () => authedClient.request<ReviewSummary>('/v1/admin/reviews/summary'),
  });
}

export function useQualityFlagQueue() {
  return useQuery({
    queryKey: keys.reviews.qualityFlags(),
    queryFn: () => authedClient.request<QualityFlagQueue>('/v1/admin/quality/flags'),
  });
}

/** The circumvention watch (12-content/15 §7.5): views against bookings, a signal, never a penalty. */
export function useProfileDivergence(days: number) {
  return useQuery({
    queryKey: keys.reviews.divergence(days),
    queryFn: () =>
      authedClient.request<DivergenceReport>(
        `/v1/admin/quality/profile-divergence?days=${days}`,
      ),
  });
}

function useReviewMutation<TArgs, TData>(fn: (args: TArgs) => Promise<TData>) {
  const client = useQueryClient();
  return useMutation<TData, unknown, TArgs>({
    mutationFn: fn,
    onSuccess: () => client.invalidateQueries({ queryKey: keys.reviews.all() }),
  });
}

/** `redacted_body` replaces the text and files the original for audit. Optional. */
export function usePublishReview() {
  return useReviewMutation(
    ({
      reviewId,
      redactedBody,
      note,
    }: {
      reviewId: number;
      redactedBody?: string;
      note?: string;
    }) =>
      authedClient.request<AdminReviewRow>(`/v1/admin/reviews/${reviewId}/publish`, {
        method: 'POST',
        body: { redacted_body: redactedBody ?? null, note: note ?? null },
      }),
  );
}

/** `reject` before publication, `remove` after. A reason from the closed list is mandatory. */
export function useModerateReview() {
  return useReviewMutation(
    ({
      reviewId,
      decision,
      reason,
      note,
    }: {
      reviewId: number;
      decision: 'reject' | 'remove';
      reason: string;
      /* Required, not optional. The only caller is a ConfirmAction whose reason field is
         mandatory, so an `undefined` fallback here would be a branch nothing can reach — and
         a refusal the author is told about should never arrive with nothing to tell them. */
      note: string;
    }) =>
      authedClient.request<AdminReviewRow>(`/v1/admin/reviews/${reviewId}/${decision}`, {
        method: 'POST',
        body: { reason, note },
      }),
  );
}

export function useCreateDimension() {
  return useReviewMutation(({ code, sortOrder }: { code: string; sortOrder: number }) =>
    authedClient.request<DimensionRow>('/v1/admin/rating-dimensions', {
      method: 'POST',
      body: { code, sort_order: sortOrder },
    }),
  );
}

/** Deactivate or reorder. There is no delete — a scored dimension is part of what those
 * reviews said, and the foreign key is RESTRICT. */
export function useUpdateDimension() {
  return useReviewMutation(
    ({ id, isActive, sortOrder }: { id: number; isActive?: boolean; sortOrder?: number }) =>
      authedClient.request<DimensionRow>(`/v1/admin/rating-dimensions/${id}`, {
        method: 'PATCH',
        body: { is_active: isActive, sort_order: sortOrder },
      }),
  );
}

export type AdminReviewResponseRow = components['schemas']['AdminReviewResponseRow'];
type AdminReviewResponseList = components['schemas']['AdminReviewResponseList'];

/** Pandit replies waiting, oldest first, each beside the review it answers. */
export function useReviewResponses() {
  return useQuery({
    queryKey: keys.reviews.responses(),
    queryFn: () => authedClient.request<AdminReviewResponseList>('/v1/admin/review-responses'),
  });
}

export function useModerateResponse() {
  return useReviewMutation(({ id, decision }: { id: number; decision: 'publish' | 'reject' }) =>
    authedClient.request<AdminReviewResponseRow>(`/v1/admin/review-responses/${id}/${decision}`, {
      method: 'POST',
    }),
  );
}
